import i18n from "i18next";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { getProductChangeRequestPriorityLabels } from "src/api/generated/erp/db/types/enums/productChangeRequestPriority.ts";
import { FormAsyncUserSelectField } from "src/components/views/users/FormAsyncUserSelectField.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import {
	ProductChangeRequestEditApi,
	ProductChangeRequestEditApi_FormInitData,
} from "src/api/generated/erp/productChanges/api/productChangeRequestEditApi.ts";
import { ProductChangeRequest } from "src/api/generated/erp/db/types/tables/productChangeRequest.ts";
import { ProductChangeCategory } from "src/api/generated/erp/db/types/tables/productChangeCategory.ts";
import { FormCommonProps } from "../../../../common/forms/types.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export type ProductChangeRequestDialogMode = "my" | "manage";

export interface ProductChangeRequestFormProps extends FormCommonProps<number> {
	productChangeRequestId: number;
	mode: ProductChangeRequestDialogMode;
}

export const ProductChangeRequestForm = ({
	productChangeRequestId,
	mode,
	onCompleted,
	onFormEdited,
}: ProductChangeRequestFormProps) => {
	const canEditAll = mode === "manage";

	return (
		<AsyncForm<ProductChangeRequestEditApi_FormInitData, ProductChangeRequest, number>
			fetch={() => ProductChangeRequestEditApi.getInitData({ productChangeRequestId })}
			getDefaultValues={(data) => {
				if (!data.productChangeRequest) {
					throw new Error("Product change request not found");
				}
				return data.productChangeRequest;
			}}
			submit={async (values) => {
				if (canEditAll) {
					await ProductChangeRequestEditApi.updateAsManager({ productChangeRequest: values });
				} else {
					await ProductChangeRequestEditApi.updateMyRequest({
						productChangeRequestId: values.productChangeRequestId,
						title: values.title,
						description: values.description,
						categoryId: values.categoryId,
					});
				}
				return productChangeRequestId;
			}}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			columns={canEditAll ? 2 : 1}
			agentContext={{
				key: defineAgentUiContextKey("erp.productChanges.productChangeRequest.form"),
				data: {
					productChangeRequestId: productChangeRequestId,
					mode: mode,
				},
			}}
			render={({ control, data: { categoryOptions } }) => (
				<>
					<FormTextField
						control={control}
						name={"title"}
						label={i18n.t("title")}
						rules={requireRule()}
						spanGridColumns
						autoFocus
					/>
					<FormTextField
						control={control}
						name={"description"}
						label={i18n.t("description")}
						rules={requireRule()}
						multiline
						minRows={3}
						spanGridColumns
					/>
					<FormSelectField
						control={control}
						name={"categoryId"}
						label={i18n.t("category")}
						options={categoryOptions}
						getOptionKey={(o: ProductChangeCategory) => o.productChangeCategoryId}
						getOptionLabel={(o) => o.name}
						disableClearable
						rules={requireRule()}
					/>
					{canEditAll && (
						<>
							<FormAsyncUserSelectField
								control={control}
								name={"responsiblePersonId"}
								label={i18n.t("responsible_person")}
							/>
							<FormEnumSelectField
								control={control}
								name={"priority"}
								label={i18n.t("priority")}
								options={getProductChangeRequestPriorityLabels()}
							/>
							<FormNumberField control={control} name={"costEstimate"} label={i18n.t("cost_estimate")} />
							<FormTextField
								control={control}
								name={"preEvaluationText"}
								label={i18n.t("pre_evaluation")}
								multiline
								minRows={3}
								spanGridColumns
							/>
							<FormTextField
								control={control}
								name={"requirementSpecificationText"}
								label={i18n.t("requirement_specification")}
								multiline
								minRows={3}
								spanGridColumns
							/>
						</>
					)}
				</>
			)}
		/>
	);
};
